import { useCallback, useEffect, useRef } from 'react'
import type { ElementLayer } from '@/api/types/stowed-element'
import type { FlushOptions } from '@/api/queues/group-action-queue'
import { GroupActionQueue, type GroupActionQueueOptions } from '@/api/queues/group-action-queue'
import type {
  GroupCommandPayload,
  RepositionCommandPayload,
  RestructureCommandPayload,
  UngroupCommandPayload,
} from '@/api/types/group-commands'

/** What a caller can attach to one queued group request. */
export type EnqueueGroupActionOptions = {
  /** Asked right before the request is sent; false drops it (see `QueuedGroupAction`). */
  shouldSend?: () => boolean
}

export interface UseGroupActionQueueOptions extends GroupActionQueueOptions {
  spreadId: string
  layer?: ElementLayer
}

export function useGroupActionQueue({
  spreadId,
  layer = 'userLayer',
  ...queueOpts
}: UseGroupActionQueueOptions) {
  const queueRef = useRef<GroupActionQueue | null>(null)

  useEffect(() => {
    queueRef.current = new GroupActionQueue(queueOpts)
    return () => {
      void queueRef.current?.flush()
      queueRef.current?.dispose()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spreadId])

  const enqueueGroup = useCallback(
    (payload: Omit<GroupCommandPayload, 'spreadId' | 'layer'>, opts?: EnqueueGroupActionOptions) =>
      queueRef.current?.enqueue({
        kind: 'group',
        payload: {
          spreadId,
          layer,
          ...payload,
        },
        shouldSend: opts?.shouldSend,
      }),
    [spreadId, layer]
  )

  const enqueueUngroup = useCallback(
    (payload: Omit<UngroupCommandPayload, 'spreadId' | 'layer'>) =>
      queueRef.current?.enqueue({
        kind: 'ungroup',
        payload: {
          spreadId,
          layer,
          ...payload,
        },
      }),
    [spreadId, layer]
  )

  const enqueueRestructure = useCallback(
    (
      payload: Omit<RestructureCommandPayload, 'spreadId' | 'layer'>,
      opts?: EnqueueGroupActionOptions
    ) =>
      queueRef.current?.enqueue({
        kind: 'restructure',
        payload: {
          spreadId,
          layer,
          ...payload,
        },
        shouldSend: opts?.shouldSend,
      }),
    [spreadId, layer]
  )

  const enqueueReposition = useCallback(
    (payload: Omit<RepositionCommandPayload, 'spreadId' | 'layer'>) =>
      queueRef.current?.enqueue({
        kind: 'reposition',
        payload: {
          spreadId,
          layer,
          ...payload,
        },
      }),
    [spreadId, layer]
  )

  const flush = useCallback(
    (opts?: FlushOptions) => queueRef.current?.flush(opts) ?? Promise.resolve(),
    []
  )

  const isBusy = useCallback(() => queueRef.current?.isBusy ?? false, [])

  return { enqueueGroup, enqueueUngroup, enqueueRestructure, enqueueReposition, flush, isBusy }
}
